import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Badge,
  Alert,
  Descriptions,
  Timeline,
  Divider
} from 'antd';
import {
  ReloadOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  EditOutlined
} from '@ant-design/icons';
import TenantInitWizard from './TenantInitWizard';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3007/api';

const { Title, Text } = Typography;

interface TenantState {
  tenantCode: string;
  tenantName: string;
  adminUser: {
    username: string;
    password: string;
    email?: string;
  };
  services: {
    sapB1?: any;
    documentParsing?: any;
    chatbi?: any;
  };
  dataInit: {
    includeModeling: boolean;
    includeInstructions: boolean;
    includeTemplates: boolean;
    includePermissions: boolean;
    syncToRemote: boolean;
  };
  status: 'pending' | 'initializing' | 'completed' | 'failed' | 'paused';
  currentStep: string;
  completedSteps: number;
  totalSteps: number;
  steps: Array<{
    id: string;
    name: string;
    description: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress: number;
    message?: string;
    error?: string;
    startTime?: string;
    endTime?: string;
    duration?: number;
  }>;
  startTime: string;
  endTime?: string;
  totalDuration?: number;
  lastUpdated: string;
  error?: string;
  retryCount: number;
  maxRetries: number;
}

const CreatingTenantsManager: React.FC = () => {
  const [tenants, setTenants] = useState<TenantState[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantState | null>(null);
  const [retryLoading, setRetryLoading] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantState | null>(null);

  // 加载创建中的租户列表
  const loadCreatingTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/tenants/creating`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      
      if (result.success) {
        setTenants(result.data);
      } else {
        message.error('加载创建中租户失败');
      }
    } catch (error) {
      console.error('加载创建中租户失败:', error);
      message.error('加载创建中租户失败');
    } finally {
      setLoading(false);
    }
  };

  // 重试租户创建
  const handleRetry = async (tenantCode: string) => {
    setRetryLoading(tenantCode);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/tenants/${tenantCode}/retry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      
      if (result.success) {
        message.success('租户创建重试已开始');
        loadCreatingTenants();
      } else {
        message.error(result.message || '重试失败');
      }
    } catch (error) {
      console.error('重试失败:', error);
      message.error('重试失败');
    } finally {
      setRetryLoading(null);
    }
  };

  // 删除租户状态
  const handleDelete = async (tenantCode: string) => {
    setDeleteLoading(tenantCode);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/tenants/${tenantCode}/state`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      
      if (result.success) {
        message.success('租户状态已删除');
        loadCreatingTenants();
      } else {
        message.error(result.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    } finally {
      setDeleteLoading(null);
    }
  };

  // 查看详情
  const handleViewDetail = (tenant: TenantState) => {
    setSelectedTenant(tenant);
    setDetailVisible(true);
  };

  // 更改配置
  const handleEditConfig = (tenant: TenantState) => {
    setEditingTenant(tenant);
    setConfigModalVisible(true);
  };

  // 关闭配置模态框
  const handleConfigModalClose = () => {
    setConfigModalVisible(false);
    setEditingTenant(null);
    // 刷新租户列表
    loadCreatingTenants();
  };

  // 获取状态标签
  const getStatusTag = (status: string) => {
    const statusMap = {
      pending: { color: 'default', text: '等待中', icon: <ClockCircleOutlined /> },
      initializing: { color: 'processing', text: '创建中', icon: <PlayCircleOutlined /> },
      completed: { color: 'success', text: '已完成', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', text: '失败', icon: <ExclamationCircleOutlined /> },
      paused: { color: 'warning', text: '已暂停', icon: <PauseCircleOutlined /> }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // 获取步骤状态标签
  const getStepStatusTag = (status: string) => {
    const statusMap = {
      pending: { color: 'default', text: '等待' },
      running: { color: 'processing', text: '运行中' },
      completed: { color: 'success', text: '完成' },
      failed: { color: 'error', text: '失败' }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 格式化时间
  const formatTime = (timeStr: string) => {
    return new Date(timeStr).toLocaleString('zh-CN');
  };

  // 格式化持续时间
  const formatDuration = (duration?: number) => {
    if (!duration) return '-';
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}小时${minutes % 60}分钟`;
    } else if (minutes > 0) {
      return `${minutes}分钟${seconds % 60}秒`;
    } else {
      return `${seconds}秒`;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '租户代码',
      dataIndex: 'tenantCode',
      key: 'tenantCode',
      render: (text: string) => <Text code>{text}</Text>
    },
    {
      title: '租户名称',
      dataIndex: 'tenantName',
      key: 'tenantName'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status)
    },
    {
      title: '进度',
      key: 'progress',
      render: (record: TenantState) => (
        <div>
          <Progress 
            percent={Math.round((record.completedSteps / record.totalSteps) * 100)} 
            size="small" 
            status={record.status === 'failed' ? 'exception' : 'active'}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.completedSteps}/{record.totalSteps}
          </Text>
        </div>
      )
    },
    {
      title: '当前步骤',
      dataIndex: 'currentStep',
      key: 'currentStep',
      render: (stepId: string, record: TenantState) => {
        const step = record.steps.find(s => s.id === stepId);
        return step ? step.name : stepId;
      }
    },
    {
      title: '重试次数',
      key: 'retryCount',
      render: (record: TenantState) => (
        <Badge 
          count={record.retryCount} 
          showZero 
          style={{ backgroundColor: record.retryCount >= record.maxRetries ? '#ff4d4f' : '#52c41a' }}
        />
      )
    },
    {
      title: '最后更新',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      render: (time: string) => formatTime(time)
    },
    {
      title: '操作',
      key: 'actions',
      render: (record: TenantState) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditConfig(record)}
          >
            更改配置
          </Button>
          {record.status === 'failed' && record.retryCount < record.maxRetries && (
            <Button
              type="link"
              icon={<PlayCircleOutlined />}
              loading={retryLoading === record.tenantCode}
              onClick={() => handleRetry(record.tenantCode)}
            >
              重试
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个租户状态吗？"
            onConfirm={() => handleDelete(record.tenantCode)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deleteLoading === record.tenantCode}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 统计信息
  const stats = {
    total: tenants.length,
    pending: tenants.filter(t => t.status === 'pending').length,
    initializing: tenants.filter(t => t.status === 'initializing').length,
    completed: tenants.filter(t => t.status === 'completed').length,
    failed: tenants.filter(t => t.status === 'failed').length
  };

  useEffect(() => {
    loadCreatingTenants();
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题和操作按钮 */}
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <HistoryOutlined style={{ marginRight: '8px' }} />
              创建中租户管理
            </Title>
            <Text type="secondary">管理正在创建和已创建的租户状态</Text>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadCreatingTenants}
                loading={loading}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总租户数"
              value={stats.total}
              prefix={<InfoCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="创建中"
              value={stats.initializing}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已完成"
              value={stats.completed}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="失败"
              value={stats.failed}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 租户列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={tenants}
          rowKey="tenantCode"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个租户`
          }}
        />
      </Card>

      {/* 详情模态框 */}
      <Modal
        title={`租户详情 - ${selectedTenant?.tenantCode}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {selectedTenant && (
          <div>
            {/* 基本信息 */}
            <Descriptions title="基本信息" bordered column={2} size="small">
              <Descriptions.Item label="租户代码">{selectedTenant.tenantCode}</Descriptions.Item>
              <Descriptions.Item label="租户名称">{selectedTenant.tenantName}</Descriptions.Item>
              <Descriptions.Item label="管理员用户">{selectedTenant.adminUser.username}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(selectedTenant.status)}</Descriptions.Item>
              <Descriptions.Item label="开始时间">{formatTime(selectedTenant.startTime)}</Descriptions.Item>
              <Descriptions.Item label="结束时间">
                {selectedTenant.endTime ? formatTime(selectedTenant.endTime) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="总耗时">
                {formatDuration(selectedTenant.totalDuration)}
              </Descriptions.Item>
              <Descriptions.Item label="重试次数">
                {selectedTenant.retryCount}/{selectedTenant.maxRetries}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* 错误信息 */}
            {selectedTenant.error && (
              <Alert
                message="错误信息"
                description={selectedTenant.error}
                type="error"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {/* 步骤时间线 */}
            <Title level={4}>创建步骤</Title>
            <Timeline>
              {selectedTenant.steps.map((step, index) => (
                <Timeline.Item
                  key={step.id}
                  color={
                    step.status === 'completed' ? 'green' :
                    step.status === 'failed' ? 'red' :
                    step.status === 'running' ? 'blue' : 'gray'
                  }
                  dot={
                    step.status === 'completed' ? <CheckCircleOutlined /> :
                    step.status === 'failed' ? <ExclamationCircleOutlined /> :
                    step.status === 'running' ? <PlayCircleOutlined /> : <ClockCircleOutlined />
                  }
                >
                  <div>
                    <Text strong>{step.name}</Text>
                    <div style={{ marginTop: '4px' }}>
                      {getStepStatusTag(step.status)}
                      {step.progress > 0 && (
                        <Progress percent={step.progress} size="small" style={{ marginLeft: '8px', width: '100px' }} />
                      )}
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      <Text type="secondary">{step.description}</Text>
                    </div>
                    {step.message && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="success">{step.message}</Text>
                      </div>
                    )}
                    {step.error && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="danger">{step.error}</Text>
                      </div>
                    )}
                    {step.startTime && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          开始: {formatTime(step.startTime)}
                          {step.endTime && ` | 结束: ${formatTime(step.endTime)}`}
                          {step.duration && ` | 耗时: ${formatDuration(step.duration)}`}
                        </Text>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </div>
        )}
      </Modal>

      {/* 配置编辑模态框 */}
      <Modal
        title={`更改配置 - ${editingTenant?.tenantCode}`}
        open={configModalVisible}
        onCancel={handleConfigModalClose}
        width={1200}
        footer={null}
        destroyOnClose
      >
        {editingTenant && (
          <TenantInitWizard
            tenantCode={editingTenant.tenantCode}
            onComplete={handleConfigModalClose}
            onCancel={handleConfigModalClose}
            mode="edit"
            initialData={{
              tenantCode: editingTenant.tenantCode,
              tenantName: editingTenant.tenantName,
              adminUser: editingTenant.adminUser,
              services: editingTenant.services,
              dataInit: editingTenant.dataInit
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default CreatingTenantsManager;
